import type { ReadonlyJSONValue } from "@rocicorp/zero";
import {
  type ChatTool,
  toolResultFailed,
  type ToolUseProps,
} from "../components/Chat/ChatTool";
import { Step } from "../components/Step";
import { Text } from "../components/Text";
import type { BashData } from "./Bash";

function parseInput(toolUse: ToolUseProps["toolUse"]) {
  const v = toolUse.input;
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, ReadonlyJSONValue>)
    : null;
}

function str(v: ReadonlyJSONValue | undefined): string | undefined {
  return typeof v === "string" ? v : undefined;
}

function stepFromInput(
  input: ReturnType<typeof parseInput>
): string | undefined {
  const explicit = str(input?.step);
  if (explicit) return explicit;
  const path = str(input?.path);
  if (!path) return undefined;
  const first = path.replace(/^\/+/, "").split("/")[0];
  return first && !first.startsWith(".") ? first : undefined;
}

function ReadFileHeader({ toolUse, result, data, interrupted }: ToolUseProps) {
  const input = parseInput(toolUse);
  const stepName = stepFromInput(input);
  const color = stepName
    ? (data as BashData | undefined)?.getStepColor(stepName)
    : undefined;

  return (
    <Step
      color={color ? `var(--${color}-600)` : undefined}
      icon="notebook-text"
      name={stepName}
      done={!!result}
      interrupted={interrupted}
      pastParticiple={str(input?.completedDescription) ?? "Read"}
      failure={
        result && toolResultFailed(result)
          ? (str(input?.failedDescription) ?? "Failed")
          : undefined
      }
      inProgress={
        str(input?.activeDescription)
          ? `${str(input?.activeDescription)}…`
          : "Reading…"
      }
    />
  );
}

function ReadFileBody({ toolUse, result }: ToolUseProps) {
  const input = parseInput(toolUse);
  const path = str(input?.path);
  const output =
    result?.output && typeof result.output === "object"
      ? (result.output as Record<string, ReadonlyJSONValue>)
      : null;
  const content = str(output?.content);
  const error = str(output?.error);

  return (
    <Text variant="code-small" color="var(--text-secondary)">
      {path && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {path}
        </pre>
      )}
      {content !== undefined && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {content}
        </pre>
      )}
      {error !== undefined && (
        <pre
          style={{
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "var(--red-600)",
          }}
        >
          {error}
        </pre>
      )}
    </Text>
  );
}

export const readFile = (data: BashData): ChatTool => ({
  name: "readFile",
  Header: ReadFileHeader,
  Body: ReadFileBody,
  data,
});
